<template>
    <div class="flex min-h-[70vh] bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Sidebar Navigation -->
        <nav class="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50">
            <div class="px-4 py-5 border-b border-gray-200">
                <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wider">Settings</h2>
                <p class="text-xs text-gray-400 mt-1 truncate">{{ agencyName }}</p>
            </div>
            <ul class="py-2">
                <li v-for="section in sections" :key="section.id">
                    <button
                        @click="activeSection = section.id"
                        :class="[
                            'w-full flex items-center px-4 py-3 text-sm transition-colors duration-150',
                            activeSection === section.id
                                ? 'bg-logo-light text-logo-dark font-medium border-r-2 border-logo'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        ]"
                    >
                        <span v-html="section.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                        <span>{{ section.label }}</span>
                        <span
                            v-if="section.id === 'notifications' && hasUnsetPush"
                            class="ml-auto w-2 h-2 rounded-full bg-gold"
                        ></span>
                    </button>
                </li>
            </ul>
        </nav>

        <!-- Content Area -->
        <div class="flex-1 min-w-0">
            <!-- Section Header -->
            <div class="px-8 py-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h1 class="text-xl font-semibold text-gray-900">{{ currentSection.label }}</h1>
                    <p class="mt-1 text-sm text-gray-500">{{ currentSection.description }}</p>
                </div>
                <transition name="fade">
                    <span v-if="saving" class="inline-flex items-center text-sm text-gray-400">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-logo" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Saving...
                    </span>
                </transition>
            </div>

            <!-- Notifications Section -->
            <div v-show="activeSection === 'notifications'" class="px-8 py-6">
                <!-- Push Permission Banner -->
                <div v-if="pushPermission === 'default'" class="mb-6 p-4 bg-logo-light bg-opacity-30 border border-logo-light rounded-lg flex items-center justify-between">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-logo mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        <div>
                            <p class="text-sm font-medium text-logo-dark">Enable Push Notifications</p>
                            <p class="text-xs text-gray-600 mt-0.5">Get real-time desktop alerts for assignment updates.</p>
                        </div>
                    </div>
                    <button @click="enablePush" class="ml-4 px-4 py-2 bg-logo text-white text-sm font-medium rounded-md hover:bg-logo-dark transition-colors">
                        Enable
                    </button>
                </div>
                <div v-else-if="pushPermission === 'denied'" class="mb-6 p-4 bg-gold-light bg-opacity-30 border border-gold-light rounded-lg">
                    <div class="flex items-center">
                        <svg class="w-5 h-5 text-gold-dark mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <div>
                            <p class="text-sm font-medium text-gold-dark">Push Notifications Blocked</p>
                            <p class="text-xs text-gray-600 mt-0.5">Enable them in your browser settings to receive desktop alerts.</p>
                        </div>
                    </div>
                </div>

                <div class="max-w-lg">
                    <div class="space-y-1">
                        <!-- Table Header -->
                        <div class="flex items-center py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <span class="flex-1">Event</span>
                            <span class="w-16 text-center">Email</span>
                            <span class="w-16 text-center">Push</span>
                        </div>

                        <!-- Notification Rows -->
                        <div
                            v-for="event in notificationEvents"
                            :key="event.key"
                            class="flex items-center py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-medium text-gray-900">{{ event.label }}</p>
                                <p class="text-xs text-gray-500 mt-0.5">{{ event.description }}</p>
                            </div>
                            <div class="w-16 flex justify-center">
                                <button
                                    type="button"
                                    @click="togglePref(event.key, 'email')"
                                    :class="[
                                        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2',
                                        prefs[event.key].email ? 'bg-logo' : 'bg-gray-200'
                                    ]"
                                    role="switch"
                                    :aria-checked="prefs[event.key].email"
                                    :data-notification-toggle="event.key + '-email'"
                                >
                                    <span
                                        :class="[
                                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                            prefs[event.key].email ? 'translate-x-4' : 'translate-x-0'
                                        ]"
                                    ></span>
                                </button>
                            </div>
                            <div class="w-16 flex justify-center">
                                <button
                                    type="button"
                                    @click="togglePref(event.key, 'push')"
                                    :disabled="pushPermission !== 'granted'"
                                    :class="[
                                        'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2',
                                        pushPermission !== 'granted' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                                        prefs[event.key].push ? 'bg-logo' : 'bg-gray-200'
                                    ]"
                                    role="switch"
                                    :aria-checked="prefs[event.key].push"
                                    :data-notification-toggle="event.key + '-push'"
                                >
                                    <span
                                        :class="[
                                            'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                            prefs[event.key].push ? 'translate-x-4' : 'translate-x-0'
                                        ]"
                                    ></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Agency Info Section -->
            <div v-show="activeSection === 'agency-info'" class="px-8 py-6">
                <div class="max-w-lg space-y-4">
                    <a :href="agencyPath + '/edit'" class="flex items-center p-4 border border-gray-200 rounded-lg hover:border-logo-light hover:bg-gray-50 transition-colors group">
                        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 group-hover:bg-logo-light flex items-center justify-center transition-colors">
                            <svg class="w-5 h-5 text-gray-500 group-hover:text-logo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                        </div>
                        <div class="ml-4 flex-1">
                            <p class="text-sm font-medium text-gray-900">Agency Information</p>
                            <p class="text-xs text-gray-500">Update name, contact details, and other agency info</p>
                        </div>
                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </a>

                    <a :href="emrPath" class="flex items-center p-4 border border-gray-200 rounded-lg hover:border-logo-light hover:bg-gray-50 transition-colors group">
                        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 group-hover:bg-logo-light flex items-center justify-center transition-colors">
                            <svg class="w-5 h-5 text-gray-500 group-hover:text-logo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                            </svg>
                        </div>
                        <div class="ml-4 flex-1">
                            <p class="text-sm font-medium text-gray-900">EMR System</p>
                            <p class="text-xs text-gray-500">Manage your Electronic Medical Record system</p>
                        </div>
                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </a>

                    <a :href="agencyPath + '/baa'" class="flex items-center p-4 border border-gray-200 rounded-lg hover:border-logo-light hover:bg-gray-50 transition-colors group">
                        <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 group-hover:bg-logo-light flex items-center justify-center transition-colors">
                            <svg class="w-5 h-5 text-gray-500 group-hover:text-logo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <div class="ml-4 flex-1">
                            <p class="text-sm font-medium text-gray-900">Business Associate Agreement</p>
                            <p class="text-xs text-gray-500">Download the HIPAA Business Associate Agreement</p>
                        </div>
                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </a>
                </div>
            </div>

            <!-- Billing Section -->
            <div v-show="activeSection === 'billing'" class="px-8 py-6">
                <div class="max-w-lg space-y-6">
                    <div class="p-4 border border-gray-200 rounded-lg">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-medium text-gray-900">Billing Frequency</p>
                                <p class="text-xs text-gray-500 mt-1">{{ billingFrequency }}</p>
                            </div>
                            <button
                                v-if="isSiteAdmin"
                                @click="toggleBillingFrequency"
                                :disabled="changingFrequency"
                                class="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <svg v-if="changingFrequency" class="animate-spin -ml-0.5 mr-2 h-3.5 w-3.5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                </svg>
                                Switch to {{ isWeeklyBilling ? 'Per Order' : 'Weekly' }}
                            </button>
                        </div>
                    </div>

                    <div class="p-4 border border-gray-200 rounded-lg">
                        <p class="text-sm font-medium text-gray-900 mb-3">Payment Method</p>
                        <store-card :agency="agency" :stripe-configs="stripeConfigs"></store-card>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <a
                    :href="agencyPath"
                    class="inline-flex items-center px-5 py-2 bg-logo text-white text-sm font-medium rounded-md hover:bg-logo-dark focus:outline-none focus:ring-2 focus:ring-logo focus:ring-offset-2 transition-colors"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Dashboard
                </a>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        agencyId: {
            type: Number,
            required: true
        },
        agencyName: {
            type: String,
            required: true
        },
        agencyPath: {
            type: String,
            required: true
        },
        emrPath: {
            type: String,
            required: true
        },
        billingFrequency: {
            type: String,
            default: 'Per Order'
        },
        initialPrefs: {
            type: Object,
            required: true
        },
        agency: {
            type: Object,
            required: true
        },
        stripeConfigs: {
            type: Object,
            required: true
        },
        initialSection: {
            type: String,
            default: 'notifications'
        },
        isSiteAdmin: {
            type: Boolean,
            default: false
        },
        isWeeklyBilling: {
            type: Boolean,
            default: false
        },
        billingFrequencyUrl: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            activeSection: this.initialSection,
            prefs: JSON.parse(JSON.stringify(this.initialPrefs)),
            saving: false,
            saveTimeout: null,
            pushPermission: 'default',
            changingFrequency: false,
            sections: [
                {
                    id: 'notifications',
                    label: 'Notifications',
                    description: 'Choose how you want to be notified about assignment updates.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>',
                },
                {
                    id: 'agency-info',
                    label: 'Agency Info',
                    description: 'Manage your agency information and documents.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>',
                },
                {
                    id: 'billing',
                    label: 'Billing',
                    description: 'Payment method and billing preferences.',
                    icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>',
                },
            ],
            notificationEvents: [
                { key: 'problem_created', label: 'Problem Created', description: 'When a coder reports a problem with an assignment' },
                { key: 'assignment_completed', label: 'Assignment Completed', description: 'When a coder completes an assignment' },
                { key: 'urgent_completed', label: 'Urgent Assignment Completed', description: 'When an urgent assignment is completed' },
            ],
        };
    },

    computed: {
        currentSection() {
            return this.sections.find(s => s.id === this.activeSection) || this.sections[0];
        },
        hasUnsetPush() {
            return this.pushPermission === 'default';
        },
    },

    mounted() {
        if ('Notification' in window) {
            this.pushPermission = Notification.permission;
        }
        if (this.pushPermission === 'granted' && window.PushNotifications) {
            window.PushNotifications.ensureSubscription();
        }
    },

    methods: {
        togglePref(eventKey, channel) {
            if (channel === 'push' && this.pushPermission !== 'granted') return;
            this.prefs[eventKey][channel] = !this.prefs[eventKey][channel];
            this.debouncedSave();
        },

        debouncedSave() {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }
            this.saveTimeout = setTimeout(() => {
                this.save();
            }, 1000);
        },

        async save() {
            this.saving = true;
            try {
                await axios.patch(`/agencies/${this.agencyId}/notification-settings`, {
                    notification_prefs: this.prefs,
                });
                this.$toast.success('Settings saved');
            } catch (error) {
                this.$toast.error('Failed to save settings');
            } finally {
                this.saving = false;
            }
        },

        async toggleBillingFrequency() {
            const newFrequency = this.isWeeklyBilling ? 'order' : 'weekly';
            if (!confirm(`Change billing frequency to ${this.isWeeklyBilling ? 'Per Order' : 'Weekly'}?`)) {
                return;
            }
            this.changingFrequency = true;
            try {
                await axios.patch(this.billingFrequencyUrl, [newFrequency]);
                this.$toast.success('Billing frequency updated');
                window.location.reload();
            } catch (error) {
                this.$toast.error('Failed to update billing frequency');
                this.changingFrequency = false;
            }
        },

        async enablePush() {
            if (!window.PushNotifications) {
                this.$toast.error('Push notifications are not available');
                return;
            }
            try {
                const result = await window.PushNotifications.subscribe();
                this.pushPermission = Notification.permission;
                if (result) {
                    this.$toast.success('Desktop notifications enabled');
                } else if (Notification.permission === 'denied') {
                    this.$toast.error('Notifications were blocked by your browser');
                }
            } catch (e) {
                console.error('Push subscription failed:', e);
                this.$toast.error('Failed to enable notifications. Please try again later.');
            }
        },
    },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
